import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  VStack,
  Heading,
  useColorModeValue,
  Divider,
  Text,
  HStack,
} from '@chakra-ui/react';
import { ChatCircleText } from '@phosphor-icons/react';
import CommentInput from './CommentInput';
import CommentList from './CommentList';
import CommentImagePreviewModal from '../CommentImagePreviewModal';
import { useAuth } from '../../hooks/useAuth';
import { useComments } from '../../hooks/comments/useComments';
import { useCommentActions } from '../../hooks/comments/useCommentActions';
import { useThreadCollapse } from '../../hooks/comments/useThreadCollapse';
import { useScrollPosition } from '../../hooks/comments/useScrollPosition';

const CommentSection = ({ siteId }) => {
  const { user } = useAuth();
  const [selectedImage, setSelectedImage] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const commentRefs = useRef({});

  // Color mode values
  // Use semantic tokens directly where needed, e.g., bg="bg.surface", borderColor="border.default"
  // const borderColor = useColorModeValue('ancient.stone', 'whiteAlpha.300'); // Replaced with border.default
  // const bgColor = useColorModeValue('white', 'gray.800'); // Replaced with bg.surface
  const highlightBg = useColorModeValue('brand.primary.50', 'gray.700'); // Use theme color for highlight, adjust dark mode if needed
  // const boxShadow = useColorModeValue('0 2px 4px rgba(0,0,0,0.05)', '0 2px 4px rgba(0,0,0,0.2)'); // Replaced with theme shadow token

  // Custom hooks
  const { comments, loadingComments, fetchComments, setComments } = useComments(siteId);
  const { handleLike, addComment, addReply } = useCommentActions(siteId, user, comments, setComments, fetchComments);
  const { collapsedThreads, toggleThread, expandThreads } = useThreadCollapse(comments);
  
  // Fetch comments on mount
  useEffect(() => {
    if (siteId) {
      fetchComments();
    }
  }, [siteId, fetchComments]);

  // Find parent comment IDs recursively
  const findParentIds = (commentId, items = comments) => {
    const parents = [];
    const findParent = (items) => {
      for (const item of items) {
        if (item.id === commentId) {
          return true;
        }
        if (item.replies?.length > 0) {
          const found = findParent(item.replies);
          if (found) {
            parents.push(item.id);
            return true;
          }
        }
      }
      return false;
    };
    findParent(items);
    return parents;
  };

  const { saveScrollPosition, restoreScrollPosition, handleHashScroll } = useScrollPosition(
    commentRefs,
    findParentIds,
    expandThreads
  );

  // Handle URL hash for comment navigation
  useEffect(() => {
    handleHashScroll(comments, highlightBg);
  }, [comments, handleHashScroll, highlightBg]);

  // Handle reply click with scroll position maintenance
  const handleReplyClick = (commentId) => {
    const scrollPosition = saveScrollPosition();
    setReplyTo(commentId);
    restoreScrollPosition(scrollPosition);
  };

  return (
    <Box
      mt={{ base: 6, md: 8 }} // Responsive margin
      className="comment-section"
      bg="bg.surface" // Use theme token
      borderRadius="lg" // Use theme radius token implicitly
      borderWidth="1px" // Use theme border width token implicitly
      borderColor="border.default" // Use theme token
      boxShadow="md" // Use theme shadow token
      overflow="hidden"
    >
      {/* Header */}
      {/* Header */}
      <Box
        bg="bg.accent.subtle" // Use theme token for header background
        p={{ base: 4, md: 6 }} // Responsive padding
        borderBottomWidth="1px" // Use theme border width token implicitly
        borderColor="border.default" // Use theme token
      >
        <HStack spacing={3} mb={4}>
          <ChatCircleText 
            size={28} 
            weight="duotone"
            color="brand.primary.500" // Use theme color token
          />
          <Heading 
            size="md"
            textStyle="hieroglyph"
            color="text.default" // Use theme text color
          >
            Discussion
          </Heading>
        </HStack>

        <Text
          fontSize="sm"
          color="text.muted" // Use theme text color
          textStyle="papyrus"
        >
          Share your thoughts and insights about this archaeological site
        </Text>
      </Box>

      {/* Main Content */}
      {/* Main Content */}
      <VStack align="stretch" spacing={{ base: 4, md: 6 }} p={{ base: 4, md: 6 }}> {/* Responsive spacing & padding */}
        {/* Comment Input Form */}
        <CommentInput
          user={user}
          onSubmit={addComment}
          siteId={siteId}
        />

        {comments.length > 0 && (
          <Divider className="ancient-divider" />
        )}

        {/* Comments List */}
        <CommentList
          comments={comments}
          loadingComments={loadingComments}
          user={user}
          siteId={siteId}
          onLike={handleLike}
          onReply={{ addReply, setSelectedImage }}
          replyTo={replyTo}
          handleReplyClick={handleReplyClick}
          collapsedThreads={collapsedThreads}
          toggleThread={toggleThread}
        />
      </VStack>

      {/* Image Preview Modal */}
      <CommentImagePreviewModal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageUrl={selectedImage}
      />
    </Box>
  );
};

export default CommentSection;
